import { useChat } from "@ai-sdk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DefaultChatTransport,
  type DynamicToolUIPart,
  generateId,
  isToolUIPart,
  type ToolUIPart,
  type UIMessage,
} from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chatHistoryKeys } from "../query-keys";
import {
  chatMessagesQueryOptions,
  seedPendingChatMessagesRoute,
} from "../query-options";
import type { AuthInfo, OnToolFinish } from "../types";
import { useAuthInfo } from "./use-auth-info";
import type { ChatHistoryItem } from "./use-chat-history";
import { isAuthError, useChatHistoryMutations } from "./use-chat-history";

const NEW_THREAD_TITLE = "New thread";

function isFinishedTool(
  part: UIMessage["parts"][number]
): part is ToolUIPart | DynamicToolUIPart {
  return isToolUIPart(part) && part.state.startsWith("output-");
}

type UseChatSessionOptions = {
  baseUrl: string;
  viewerId: string;
  onAuthError?: () => void;
  getBody?: () => object;
  initialChatId?: string;
  initialMessages?: UIMessage[];
  onChatPersisted?: (chatId: string) => void;
  onToolFinish?: OnToolFinish;
};

type ChatSession = {
  chatId: string;
  messages: UIMessage[];
  isStreaming: boolean;
  stop: () => void;
  newChat: () => void;
  deleteChat: (id: string) => Promise<boolean>;
  deleteAll: () => Promise<boolean>;
  send: (text: string) => void;
  retryAuth: () => void;
  authInfoMap: Map<string, AuthInfo>;
};

type StreamDataPart = { type: string; data?: unknown };

function parseTitleUpdate(
  dataPart: StreamDataPart
): { chatId: string; title: string } | null {
  if (dataPart.type !== "data-title-update") {
    return null;
  }
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const p = dataPart.data as { chatId?: string; title?: string } | undefined;
  return typeof p?.chatId === "string" && typeof p?.title === "string"
    ? { chatId: p.chatId, title: p.title }
    : null;
}

function findLastUserMessageIndex(messages: UIMessage[]) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "user") {
      return i;
    }
  }
  return -1;
}

function extractLastUserText(messages: UIMessage[]): string | null {
  const idx = findLastUserMessageIndex(messages);
  if (idx < 0) {
    return null;
  }
  const text = (messages[idx]?.parts ?? [])
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("")
    .trim();
  return text || null;
}

export function useChatSession(options: UseChatSessionOptions): ChatSession {
  const { baseUrl, viewerId, getBody, initialChatId } = options;

  // ChatProvider is keyed by org:project, so the hook stays mounted across
  // chat navigations. Keying by chatId would remount the hook, which makes
  // useChat treat `messages: []` as authoritative and wipe an in-flight
  // stream — specifically, the first send from the index route would flash
  // empty after onChatPersisted updates the URL. We keep the mount alive and
  // rely on useChat's keyed-by-id store for continuity; resetSession and
  // activeChatIdRef handle the cleanup that a remount would have given us.
  const stableRefs = useRef({
    getBody,
    onChatPersisted: options.onChatPersisted,
    hasNavigatedForChat: !!initialChatId,
  });
  stableRefs.current.getBody = getBody;
  stableRefs.current.onChatPersisted = options.onChatPersisted;

  const [localChatId, setLocalChatId] = useState(generateId);
  const chatId = initialChatId ?? localChatId;

  const {
    authInfoMap,
    handleDataPart: handleAuthDataPart,
    stopPolling: stopAuthPolling,
    clear: clearAuthInfo,
  } = useAuthInfo();

  const hasToolFiredRef = useRef(false);
  const pendingSendRef = useRef<string | null>(null);
  const streamChatIdRef = useRef<string | null>(null);
  const ignoredTerminalCountRef = useRef(0);

  // Render-time guard: when chatId changes (sidebar navigation between
  // chats, newChat, or initialChatId prop change), clear per-chat refs so
  // we don't carry a pending send or first-tool-fired flag across chats.
  // Also re-anchor hasNavigatedForChat to !!initialChatId so navigation
  // into an existing chat doesn't cause the next send to re-seed its
  // messages cache or re-fire onChatPersisted.
  const activeChatIdRef = useRef(chatId);
  if (activeChatIdRef.current !== chatId) {
    activeChatIdRef.current = chatId;
    pendingSendRef.current = null;
    hasToolFiredRef.current = false;
    stableRefs.current.hasNavigatedForChat = !!initialChatId;
  }

  const queryClient = useQueryClient();
  const listKey = useMemo(() => chatHistoryKeys.list(viewerId), [viewerId]);
  const messagesKey = useMemo(
    () => chatHistoryKeys.messages(viewerId, chatId),
    [viewerId, chatId]
  );

  // Fall back to the React Query cache (populated by route loaders) when
  // the caller doesn't provide initialMessages. Recompute per render so
  // chatId transitions hand fresh data to useChat — a mount-time freeze
  // would let a stale A-chat snapshot leak into B-chat's fresh Chat
  // instance. useChat only consumes `messages` when creating the store
  // for a new id, so per-render reads don't re-seed mid-session.
  let initialMessages: UIMessage[] | undefined;
  if (options.initialMessages !== undefined) {
    initialMessages = options.initialMessages;
  } else if (initialChatId) {
    initialMessages = queryClient.getQueryData<UIMessage[]>(
      chatMessagesQueryOptions({ baseUrl, viewerId, chatId: initialChatId })
        .queryKey
    );
  }
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${baseUrl}/api/chat`,
        credentials: "include",
        body: () => stableRefs.current.getBody?.() ?? {},
      }),
    [baseUrl]
  );

  const { deleteChatAsync, deleteAllAsync } = useChatHistoryMutations({
    baseUrl,
    viewerId,
    onAuthError: options.onAuthError,
  });

  const upsertHistoryItem = useCallback(
    (id: string, title: string) => {
      const now = new Date().toISOString();
      queryClient.setQueryData<ChatHistoryItem[]>(listKey, (old) => {
        const nextHistory = old ?? [];
        const currentItem = nextHistory.find(
          (historyItem) => historyItem.id === id
        );
        const nextItem: ChatHistoryItem = currentItem
          ? { ...currentItem, title, updatedAt: now }
          : { id, title, createdAt: now, updatedAt: now };

        return [
          nextItem,
          ...nextHistory.filter((historyItem) => historyItem.id !== id),
        ];
      });
    },
    [listKey, queryClient]
  );

  const {
    messages,
    sendMessage,
    status,
    stop: stopStream,
    setMessages,
  } = useChat({
    id: chatId,
    messages: initialMessages,
    transport,
    experimental_throttle: 50,
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: listKey });

      if (consumeIgnoredTerminalEvent()) {
        return;
      }

      if (isAuthError(error)) {
        options.onAuthError?.();
        return;
      }
    },
    onFinish: ({ message, messages: completeMessages }) => {
      queryClient.invalidateQueries({ queryKey: listKey });

      if (consumeIgnoredTerminalEvent()) {
        return;
      }

      const finishedChatId = streamChatIdRef.current;
      if (!finishedChatId) {
        return;
      }

      const finishedMessagesKey = chatHistoryKeys.messages(
        viewerId,
        finishedChatId
      );
      streamChatIdRef.current = null;

      queryClient.setQueryData<UIMessage[]>(
        finishedMessagesKey,
        completeMessages
      );
      queryClient.invalidateQueries({
        exact: true,
        queryKey: finishedMessagesKey,
        refetchType: "none",
      });

      const onToolFinish = options.onToolFinish;
      if (!onToolFinish) {
        return;
      }

      for (const part of message.parts) {
        if (!isFinishedTool(part)) {
          continue;
        }
        const isFirstInSession = !hasToolFiredRef.current;
        hasToolFiredRef.current = true;
        onToolFinish(part, { isFirstInSession });
      }
    },
    onData: (dataPart) => {
      const titleUpdate = parseTitleUpdate(dataPart);
      if (titleUpdate) {
        upsertHistoryItem(titleUpdate.chatId, titleUpdate.title);
        return;
      }

      handleAuthDataPart(dataPart);
    },
  });

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Mirror the current non-empty client-side view into React Query so route
  // transitions can restore the latest chat immediately. We intentionally skip
  // empty arrays because useChat briefly renders [] during chatId transitions;
  // writing that to the cache would wipe persisted history and recreate the
  // “open from history => blank until refresh” failure mode.
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }
    queryClient.setQueryData<UIMessage[]>(messagesKey, messages);
  }, [messages, messagesKey, queryClient]);

  const isStreaming = status === "streaming" || status === "submitted";
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  const consumeIgnoredTerminalEvent = useCallback(() => {
    if (ignoredTerminalCountRef.current === 0) {
      return false;
    }

    ignoredTerminalCountRef.current -= 1;
    return true;
  }, []);

  const abandonCurrentStream = useCallback(() => {
    if (isStreamingRef.current) {
      ignoredTerminalCountRef.current += 1;
    }
    streamChatIdRef.current = null;
    stopStream();
    stopAuthPolling();
  }, [stopAuthPolling, stopStream]);

  // Handle chatId transitions in one place: abandon the current stream
  // (sidebar navigation shouldn't leak a stream into the new chat), then
  // seed messages from the cache because useChat's internal module caching
  // means the recreated Chat instance may not reflect the latest cache for
  // the new id. First run is a no-op — useChat handles initial mount itself.
  const prevChatIdRef = useRef(chatId);
  useEffect(() => {
    if (prevChatIdRef.current === chatId) {
      return;
    }
    prevChatIdRef.current = chatId;
    abandonCurrentStream();
    const cached = queryClient.getQueryData<UIMessage[]>(messagesKey);
    setMessages(cached ?? []);
  }, [abandonCurrentStream, chatId, messagesKey, queryClient, setMessages]);

  // Reset to a fresh chat within the current mount. The ChatProvider is
  // keyed by org:project (not chatId), so chat transitions don't remount —
  // we clear state manually here. Used by newChat and after successful
  // deletion of the active chat.
  const resetSession = useCallback(() => {
    abandonCurrentStream();
    setLocalChatId(generateId());
    clearAuthInfo();
    hasToolFiredRef.current = false;
    pendingSendRef.current = null;
    stableRefs.current.hasNavigatedForChat = false;
  }, [abandonCurrentStream, clearAuthInfo]);

  const stop = useCallback(() => {
    pendingSendRef.current = null;
    abandonCurrentStream();
  }, [abandonCurrentStream]);

  const newChat = resetSession;

  // deleteChat/deleteAll stop the stream and fire the API call. On success
  // we call resetSession() to install a fresh localChatId so the caller's
  // navigation to the index route doesn't fall back to the deleted chat's
  // ID. On failure the user stays on the chat with the stream stopped —
  // they can retry or send a new message.
  const deleteChat = useCallback(
    async (id: string) => {
      if (id === chatId) {
        pendingSendRef.current = null;
        abandonCurrentStream();
        try {
          await deleteChatAsync(id);
          resetSession();
          return true;
        } catch {
          return false;
        }
      }

      deleteChatAsync(id).catch(() => undefined);
      return false;
    },
    [abandonCurrentStream, chatId, deleteChatAsync, resetSession]
  );

  const deleteAll = useCallback(async () => {
    pendingSendRef.current = null;
    abandonCurrentStream();
    try {
      await deleteAllAsync();
      resetSession();
      return true;
    } catch {
      return false;
    }
  }, [abandonCurrentStream, deleteAllAsync, resetSession]);

  const dispatchSend = useCallback(
    (trimmed: string) => {
      clearAuthInfo();
      const title =
        queryClient
          .getQueryData<ChatHistoryItem[]>(listKey)
          ?.find((h) => h.id === chatId)?.title ?? NEW_THREAD_TITLE;
      upsertHistoryItem(chatId, title);
      // First send for this chatId: seed the messages cache so the
      // chats/$chatId route's blocking ensureQueryData is a cache hit, then
      // fire onChatPersisted so the caller can update the URL. The hook
      // stays mounted across this transition (ChatProvider is keyed by
      // org:project), so useChat's in-flight stream is preserved.
      if (!stableRefs.current.hasNavigatedForChat) {
        seedPendingChatMessagesRoute({
          baseUrl,
          chatId,
          queryClient,
          viewerId,
        });
        stableRefs.current.hasNavigatedForChat = true;
        stableRefs.current.onChatPersisted?.(chatId);
      }
      streamChatIdRef.current = chatId;
      sendMessage({ text: trimmed });
    },
    [
      baseUrl,
      chatId,
      clearAuthInfo,
      listKey,
      queryClient,
      sendMessage,
      upsertHistoryItem,
      viewerId,
    ]
  );

  useEffect(() => {
    const pending = pendingSendRef.current;
    if (!pending) {
      return;
    }
    if (status !== "ready" && status !== "error") {
      return;
    }
    pendingSendRef.current = null;
    dispatchSend(pending);
  }, [status, dispatchSend]);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }

      if (isStreaming) {
        pendingSendRef.current = trimmed;
        abandonCurrentStream();
        return;
      }

      dispatchSend(trimmed);
    },
    [abandonCurrentStream, dispatchSend, isStreaming]
  );

  const retryAuth = useCallback(() => {
    const text = extractLastUserText(messagesRef.current);
    if (!text) {
      return;
    }
    setMessages((currentMessages) => {
      const idx = findLastUserMessageIndex(currentMessages);
      return idx >= 0 ? currentMessages.slice(0, idx) : currentMessages;
    });
    send(text);
  }, [send, setMessages]);

  return useMemo(
    () => ({
      chatId,
      messages,
      isStreaming,
      stop,
      newChat,
      deleteChat,
      deleteAll,
      send,
      retryAuth,
      authInfoMap,
    }),
    [
      authInfoMap,
      chatId,
      deleteAll,
      deleteChat,
      isStreaming,
      messages,
      newChat,
      retryAuth,
      send,
      stop,
    ]
  );
}
